import type { EmbedKind } from "@workspace/contracts/embed"
import {
  EMBED_AGENT_ATTRIBUTE,
  EMBED_LEGACY_ELEMENT,
} from "@workspace/contracts/embed/snippet"

import { ApiError, fetchAgentSnapshot, fetchSnapshot } from "./api"
import { guardSnapshot } from "./guard"
import type { Theme } from "./host"
import { render, type Mount } from "./mount"

/**
 * The tags the install snippet writes (`contracts/embed/snippet.ts`):
 * `<usereq-widget widget-id="…">` and `<usereq-group group-id="…">`. On
 * connect each fetches its active snapshot, checks the envelope, and
 * mounts the runtime into its own shadow root; on disconnect it unmounts.
 * Optional attributes: `api-url` (a staging API, the dev host page) and
 * `theme="light|dark"` (when sampling the page's background would lie).
 *
 * `<usereq-agent-widget agent-id="…">` is the previous platform's element -
 * the ONLY one its 0.2.x script defined - and the pages pasted before this
 * runtime still carry it, loading `@usereq/widget` unpinned. So this script
 * defines that name too, on the widget arm: an agent has one live chat
 * surface, so the id asks `/api/embed/agent/:id` and mounts that surface's
 * widget payload. The same `agent-id` alias is accepted on `<usereq-widget>`,
 * where `widget-id` wins when both are set - the snippet writes `widget-id`,
 * and a page that names both is saying the same thing twice at best. On the
 * old element the alias LEADS instead, because that is the id those pages
 * carry.
 *
 * What the old element's other attributes said - `preset`, `mode`,
 * `placement`, `action-text`, the orb colours - is not read here: this
 * runtime takes the look from the published surface, so a merchant's page
 * shows what the dashboard says it shows. That is the one visible change
 * when those pages move onto this script.
 *
 * Nothing here throws onto the customer's page: a tag with no live
 * snapshot, a page off the verified domains, a network that is down - each
 * is one `console.warn` and an empty element.
 */

/**
 * An element this script defines: the name, what it mounts, and the
 * attributes that can name its thing - first non-blank wins.
 */
export interface EmbedArm {
  kind: EmbedKind
  element: string
  attributes: string[]
}

export const EMBED_ARMS = {
  widget: {
    kind: "widget",
    element: "usereq-widget",
    attributes: ["widget-id", EMBED_AGENT_ATTRIBUTE],
  },
  group: {
    kind: "group",
    element: "usereq-group",
    attributes: ["group-id"],
  },
  /**
   * The previous platform's element. It mounts a widget like the tag above,
   * but the agent names the surface there, so the alias leads; `widget-id`
   * is still honoured for a page that was updated by hand.
   */
  legacy: {
    kind: "widget",
    element: EMBED_LEGACY_ELEMENT,
    attributes: [EMBED_AGENT_ATTRIBUTE, "widget-id"],
  },
} satisfies Record<string, EmbedArm>

export interface EmbedTarget {
  /** The attribute that named it - `agent-id` means the agent alias. */
  attribute: string
  id: string
}

/**
 * What a tag names, read through `read` (the element's `getAttribute`):
 * the first of the arm's attributes that carries a non-blank value, so
 * `widget-id` wins over `agent-id` on this platform's tag and `agent-id`
 * leads on the old one; null when none does. Pure, so the precedence is
 * testable without a DOM.
 */
export function embedTarget(
  arm: EmbedArm,
  read: (attribute: string) => string | null
): EmbedTarget | null {
  for (const attribute of arm.attributes) {
    const id = read(attribute)?.trim()
    if (id) return { attribute, id }
  }
  return null
}

function warn(message: string) {
  console.warn(`[usereq] ${message}`)
}

function defineEmbed(arm: EmbedArm) {
  const { kind, element, attributes } = arm
  if (customElements.get(element)) return

  class EmbedElement extends HTMLElement {
    static observedAttributes = [...attributes, "api-url", "theme"]

    #mount: Mount | null = null
    #loading = 0

    connectedCallback() {
      void this.#load()
    }

    disconnectedCallback() {
      this.#mount?.unmount()
      this.#mount = null
    }

    attributeChangedCallback(
      _name: string,
      before: string | null,
      after: string | null
    ) {
      if (before !== after && this.isConnected) void this.#load()
    }

    async #load() {
      const target = embedTarget(arm, (name) => this.getAttribute(name))
      if (!target) return
      const { attribute, id } = target
      const ticket = ++this.#loading
      this.#mount?.unmount()
      this.#mount = null

      const apiUrl = (
        this.getAttribute("api-url") ?? __USEREQ_API_URL__
      ).replace(/\/+$/, "")
      const themeAttr = this.getAttribute("theme")
      const theme: Theme | null =
        themeAttr === "light" || themeAttr === "dark" ? themeAttr : null

      let snapshot
      try {
        const api = { base: apiUrl }
        const answered =
          attribute === EMBED_AGENT_ATTRIBUTE
            ? await fetchAgentSnapshot(api, id)
            : await fetchSnapshot(api, kind, id)
        snapshot = guardSnapshot(answered, kind)
      } catch (caught) {
        if (caught instanceof ApiError) {
          warn(`${element} ${id}: ${caught.message} (${caught.code})`)
        } else {
          warn(`${element} ${id}: could not reach ${apiUrl}.`)
        }
        return
      }
      // The attribute changed while the fetch was out: that load owns the element now.
      if (ticket !== this.#loading || !this.isConnected) return
      if (!snapshot) {
        warn(`${element} ${id}: the response was not a ${kind} snapshot.`)
        return
      }

      this.#mount = render(snapshot, this, { apiUrl, theme })
    }
  }

  customElements.define(element, EmbedElement)
}

/** Register every tag. Idempotent: a page that loads the script twice gets one definition. */
export function defineElements() {
  for (const arm of Object.values(EMBED_ARMS)) defineEmbed(arm)
}
